import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/docs/portal/widget")({
	component: WidgetDocsPage,
});

function H({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<h2
			id={id}
			className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mt-14 mb-4 pt-6 border-t border-zinc-100 dark:border-zinc-800/50 scroll-mt-20 first:border-0 first:mt-0 first:pt-0"
		>
			{children}
		</h2>
	);
}

function H3({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-6 mb-2">
			{children}
		</h3>
	);
}

function Pre({ title, children }: { title?: string; children: string }) {
	return (
		<div className="rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden mb-4">
			{title && (
				<div className="px-3 py-1 text-[9px] text-zinc-500 uppercase tracking-widest border-b border-zinc-800 font-medium">
					{title}
				</div>
			)}
			<pre className="px-3 py-2 overflow-x-auto text-[11px] leading-relaxed text-zinc-300 font-mono">
				{children}
			</pre>
		</div>
	);
}

function ParamRow({
	name,
	values,
	def,
}: { name: string; values: React.ReactNode; def: React.ReactNode }) {
	return (
		<TableRow className="border-b border-zinc-100 dark:border-zinc-900/50 last:border-0">
			<TableCell className="py-1.5 pr-3">
				<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
					{name}
				</code>
			</TableCell>
			<TableCell className="py-1.5 pr-3 text-zinc-500 text-[12px]">{values}</TableCell>
			<TableCell className="py-1.5 text-zinc-400 text-[12px]">{def}</TableCell>
		</TableRow>
	);
}

function DocTable({
	headers,
	children,
}: { headers: string[]; children: React.ReactNode }) {
	return (
		<div className="rounded border border-zinc-200 dark:border-zinc-800 mb-4 text-[12px]">
			<Table>
				<TableHeader>
					<TableRow className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
						{headers.map((h) => (
							<TableHead
								key={h}
								className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto"
							>
								{h}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
					{children}
				</TableBody>
			</Table>
		</div>
	);
}

function WidgetDocsPage() {
	const { t } = useTranslation("docs");
	return (
		<div className="w-full">
			<div className="mb-4">
				<h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
					{t("widget_title")}
				</h1>
				<p className="mt-1.5 text-[13px] text-zinc-500">
					{t("widget_intro")}{" "}
					<Link to="/portals" className="text-primary hover:underline">
						{t("widget_settings_search")}
					</Link>
					.
				</p>
			</div>

			<H id="installation">{t("widget_installation")}</H>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("widget_installation_desc")}
			</p>
			<Pre>{`<iframe
  src="https://omnios.malevich.ai/widget?key=omnios_pk_...&api=https://api.omnios.malevich.ai"
  style="width:100%;border:none;min-height:480px"
  loading="lazy"
></iframe>`}</Pre>

			<H id="auto-height">{t("widget_auto_height")}</H>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("widget_auto_height_desc") }}
			/>
			<Pre>{`<script>
window.addEventListener('message', function(e) {
  if (e.data?.type === 'omnios:resize') {
    document.querySelector('iframe[src*="widget"]')
      .style.height = e.data.height + 'px';
  }
});
</script>`}</Pre>

			<H id="params">{t("widget_params")}</H>
			<DocTable
				headers={[
					t("widget_param"),
					t("widget_param_values"),
					t("widget_param_default"),
				]}
			>
				<ParamRow name="key" values={t("widget_param_key_desc")} def="—" />
				<ParamRow
					name="api"
					values={t("widget_param_api_desc")}
					def={t("widget_param_api_default")}
				/>
				<ParamRow
					name="theme"
					values={
						<>
							<code className="text-[10px]">light</code> /{" "}
							<code className="text-[10px]">dark</code> /{" "}
							<code className="text-[10px]">auto</code>
						</>
					}
					def={<code className="text-[10px]">auto</code>}
				/>
				<ParamRow
					name="accent"
					values={
						<span
							dangerouslySetInnerHTML={{
								__html: t("widget_param_accent_desc"),
							}}
						/>
					}
					def={<code className="text-[10px]">4f46e5</code>}
				/>
				<ParamRow
					name="locale"
					values={
						<>
							<code className="text-[10px]">ru</code> /{" "}
							<code className="text-[10px]">en</code>
						</>
					}
					def={<code className="text-[10px]">ru</code>}
				/>
				<ParamRow
					name="placeholder"
					values={t("widget_param_placeholder_desc")}
					def={t("widget_param_placeholder_default")}
				/>
				<ParamRow
					name="radius"
					values={t("widget_param_radius_desc")}
					def={<code className="text-[10px]">10</code>}
				/>
				<ParamRow
					name="brand"
					values={
						<>
							<code className="text-[10px]">1</code> /{" "}
							<code className="text-[10px]">0</code>
						</>
					}
					def={<code className="text-[10px]">1</code>}
				/>
				<ParamRow
					name="price"
					values={
						<>
							<code className="text-[10px]">1</code> /{" "}
							<code className="text-[10px]">0</code>
						</>
					}
					def={<code className="text-[10px]">1</code>}
				/>
				<ParamRow
					name="desc"
					values={
						<>
							<code className="text-[10px]">1</code> /{" "}
							<code className="text-[10px]">0</code>
						</>
					}
					def={<code className="text-[10px]">0</code>}
				/>
				<ParamRow
					name="max"
					values={t("widget_param_max_desc")}
					def={<code className="text-[10px]">20</code>}
				/>
				<ParamRow
					name="product_url"
					values={
						<span
							dangerouslySetInnerHTML={{
								__html: t("widget_param_product_url_desc"),
							}}
						/>
					}
					def="—"
				/>
				<ParamRow
					name="currency"
					values={t("widget_param_currency_desc")}
					def={t("widget_param_currency_default")}
				/>
			</DocTable>

			<H id="events">{t("widget_events")}</H>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("widget_events_desc") }}
			/>

			<H3>{t("widget_events_outgoing")}</H3>
			<DocTable
				headers={[
					"type",
					t("widget_events_data"),
					t("widget_events_description"),
				]}
			>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:resize</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">{"{ height: number }"}</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_event_resize")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:product_click</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">{"{ product: {...} }"}</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_event_product_click")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:navigate</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">
							{"{ url: string, product: {...} }"}
						</code>
					</TableCell>
					<td
						className="py-1 px-3 text-zinc-500"
						dangerouslySetInnerHTML={{ __html: t("widget_event_navigate") }}
					/>
				</TableRow>
			</DocTable>

			<H3>{t("widget_events_incoming")}</H3>
			<DocTable
				headers={[
					"type",
					t("widget_events_data"),
					t("widget_events_description"),
				]}
			>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:search</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">{"{ query: string }"}</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_event_search")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:config</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">{"{ config: {...} }"}</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_event_config")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:conversion</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">
							{"{ productId, orderId, revenueCents, currency }"}
						</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_event_conversion")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:add_to_cart</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">{"{ productId }"}</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_event_add_to_cart")}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">omnios:event</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						<code className="text-[10px]">
							{"{ eventType, productId, ... }"}
						</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_event_custom")}
					</TableCell>
				</TableRow>
			</DocTable>

			<H id="tracker">{t("widget_tracker_title")}</H>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("widget_tracker_intro") }}
			/>

			<H3>{t("widget_tracker_installation")}</H3>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{
					__html: t("widget_tracker_installation_desc"),
				}}
			/>
			<Pre>{`<script
  src="https://omnios.malevich.ai/omnios-tracker.js"
  data-api-key="omnios_pk_..."
  data-api-url="https://api.omnios.malevich.ai"
></script>`}</Pre>

			<H3>{t("widget_tracker_attributes")}</H3>
			<DocTable
				headers={[
					t("widget_tracker_attribute"),
					t("widget_tracker_attr_desc"),
					t("widget_tracker_attr_default"),
				]}
			>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">data-api-key</code>
					</TableCell>
					<td
						className="py-1 px-3 text-zinc-500"
						dangerouslySetInnerHTML={{
							__html: t("widget_tracker_api_key_desc"),
						}}
					/>
					<TableCell className="py-1 px-3 text-zinc-400">—</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">data-api-url</code>
					</TableCell>
					<td
						className="py-1 px-3 text-zinc-500"
						dangerouslySetInnerHTML={{
							__html: t("widget_tracker_api_url_desc"),
						}}
					/>
					<TableCell className="py-1 px-3 text-zinc-400">—</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1 px-3">
						<code className="text-[11px] font-mono">data-auto-track</code>
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-500">
						{t("widget_tracker_auto_track_desc")}
					</TableCell>
					<TableCell className="py-1 px-3 text-zinc-400">
						<code className="text-[10px]">true</code>
					</TableCell>
				</TableRow>
			</DocTable>

			<H3>{t("widget_tracker_auto_tracking")}</H3>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{
					__html: t("widget_tracker_auto_tracking_desc"),
				}}
			/>
			<ul className="text-[12px] text-zinc-500 space-y-1.5 mb-4 list-disc list-inside">
				<li>
					<strong className="text-zinc-700 dark:text-zinc-300">
						page_view
					</strong>{" "}
					— {t("widget_tracker_auto_page_view")}
				</li>
				<li>
					<strong className="text-zinc-700 dark:text-zinc-300">
						product_view
					</strong>{" "}
					— {t("widget_tracker_auto_product_view")}
					<ul className="ml-4 mt-1 space-y-0.5 list-none text-[11px] text-zinc-400 font-mono">
						<li>{'<meta property="og:product:id" content="uuid">'}</li>
						<li>{'<meta name="omnios:product_id" content="uuid">'}</li>
						<li
							dangerouslySetInnerHTML={{
								__html: t("widget_tracker_auto_product_attr"),
							}}
						/>
						<li
							dangerouslySetInnerHTML={{
								__html: t("widget_tracker_auto_product_url"),
							}}
						/>
					</ul>
				</li>
			</ul>

			<H3>{t("widget_tracker_manual")}</H3>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("widget_tracker_manual_desc") }}
			/>
			<Pre>{`// Добавление в корзину
omnios.track("add_to_cart", { product_id: "uuid" });

// Покупка
omnios.track("purchase", {
  product_id: "uuid",
  order_id: "ORD-123",
  revenue_cents: 15000,
  currency: "RUB"
});`}</Pre>

			<H3>{t("widget_tracker_event_types")}</H3>
			<DocTable
				headers={[
					"event_type",
					t("widget_events_description"),
					t("widget_tracker_event_key_fields"),
				]}
			>
				{(
					[
						[
							"page_view",
							t("widget_tracker_event_page_view"),
							"url, referrer, utm_*",
						],
						[
							"product_view",
							t("widget_tracker_event_product_view"),
							"product_id",
						],
						[
							"search",
							t("widget_tracker_event_search"),
							"query_text, results_count",
						],
						["click", t("widget_tracker_event_click"), "product_id, position"],
						[
							"impression",
							t("widget_tracker_event_impression"),
							"product_id, position",
						],
						[
							"add_to_cart",
							t("widget_tracker_event_add_to_cart"),
							"product_id",
						],
						[
							"remove_from_cart",
							t("widget_tracker_event_remove_from_cart"),
							"product_id",
						],
						["checkout", t("widget_tracker_event_checkout"), "—"],
						[
							"purchase",
							t("widget_tracker_event_purchase"),
							"product_id, order_id, revenue_cents",
						],
					] as const
				).map(([ev, desc, fields]) => (
					<TableRow key={ev}>
						<TableCell className="py-1 px-3">
							<code className="text-[11px] font-mono font-semibold">{ev}</code>
						</TableCell>
						<TableCell className="py-1 px-3 text-zinc-500">{desc}</TableCell>
						<TableCell className="py-1 px-3 text-zinc-400 font-mono text-[10px]">
							{fields}
						</TableCell>
					</TableRow>
				))}
			</DocTable>

			<H3>{t("widget_tracker_helper_methods")}</H3>
			<Pre>{`omnios.getVisitorId()  // Текущий visitor ID
omnios.getSessionId() // Текущий session ID
omnios.flush()        // Принудительная отправка буфера`}</Pre>

			<H3>{t("widget_tracker_security")}</H3>
			<ul className="text-[12px] text-zinc-500 space-y-1 mb-4 list-disc list-inside">
				<li
					dangerouslySetInnerHTML={{
						__html: t("widget_tracker_security_same_key"),
					}}
				/>
				<li>{t("widget_tracker_security_buffered")}</li>
				<li
					dangerouslySetInnerHTML={{
						__html: t("widget_tracker_security_beacon"),
					}}
				/>
				<li>{t("widget_tracker_security_limit")}</li>
			</ul>

			<div className="mt-14 pt-4 border-t border-zinc-100 dark:border-zinc-800/50 text-center">
				<p className="text-[10px] text-zinc-400 tracking-wider uppercase">
					Portal Widget & Tracker
				</p>
			</div>
		</div>
	);
}
